import React from 'react';
import { Layout, Menu, Typography, Avatar, Space, Button, Dropdown } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppstoreOutlined,
  CheckCircleOutlined,
  DashboardOutlined,
  BookOutlined,
  UserOutlined,
  DownOutlined,
  LogoutOutlined,
  RobotOutlined,
  TeamOutlined
} from '@ant-design/icons';
import { useMsal } from '@azure/msal-react';
import { useAuth } from '../hooks/useAuth';
import type { MenuProps } from 'antd';

const { Header: AntHeader } = Layout;
const { Title, Text } = Typography;

const Header: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAdmin, isStakeholder } = useAuth();
  const { instance } = useMsal();
  const location = useLocation();

  const handleLogout = () => {
    instance.logoutPopup({
      postLogoutRedirectUri: '/',
      mainWindowRedirectUri: '/'
    });
  };

  const menuItems: MenuProps['items'] = [
    {
      key: '/',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
    },
  ];

  if (isAdmin) {
    menuItems.push({
      key: '/admin',
      icon: <BookOutlined />,
      label: 'GrowthHub Admin',
    });
  }

  if (isStakeholder) {
    menuItems.push({
      key: '/stakeholder',
      icon: <TeamOutlined />,
      label: 'Stakeholder View',
    });
  }

  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    navigate(key);
  };

  const userMenu: MenuProps['items'] = [
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Sign Out',
      onClick: handleLogout
    }
  ];

  return (
    <AntHeader style={{
      position: 'sticky',
      top: 0,
      zIndex: 1000,
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      background: '#fff',
      padding: '0 40px',
      borderBottom: '1px solid #f0f0f0',
      height: '64px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.03)'
    }}>
      {/* Brand Logo */}
      <div style={{ display: 'flex', alignItems: 'center', minWidth: '200px', cursor: 'pointer' }} onClick={() => navigate('/')}>
        <div style={{
          height: 40,
          display: 'flex',
          alignItems: 'center',
          marginRight: 12
        }}>
          <img
            src="/logo.png"
            alt="CCTech"
            style={{ height: '100%', objectFit: 'contain' }}
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              const parent = e.currentTarget.parentElement;
              if (parent) {
                const fallback = document.createElement('div');
                fallback.style.width = '32px';
                fallback.style.height = '32px';
                fallback.style.backgroundColor = '#1890ff';
                fallback.style.borderRadius = '8px';
                fallback.style.display = 'flex';
                fallback.style.alignItems = 'center';
                fallback.style.justifyContent = 'center';
                fallback.innerHTML = '<span class="anticon" style="font-size: 20px; color: white;"><svg viewBox="64 64 896 896" focusable="false" data-icon="appstore" width="1em" height="1em" fill="currentColor" aria-hidden="true"><path d="M464 144H160c-8.8 0-16 7.2-16 16v304c0 8.8 7.2 16 16 16h304c8.8 0 16-7.2 16-16V160c0-8.8-7.2-16-16-16zm-52 268H212V212h200v200zm452-268H560c-8.8 0-16 7.2-16 16v304c0 8.8 7.2 16 16 16h304c8.8 0 16-7.2 16-16V160c0-8.8-7.2-16-16-16zm-52 268H612V212h200v200zM464 544H160c-8.8 0-16 7.2-16 16v304c0 8.8 7.2 16 16 16h304c8.8 0 16-7.2 16-16V560c0-8.8-7.2-16-16-16zm-52 268H212V612h200v200zm452-268H560c-8.8 0-16 7.2-16 16v304c0 8.8 7.2 16 16 16h304c8.8 0 16-7.2 16-16V560c0-8.8-7.2-16-16-16zm-52 268H612V612h200v200z"></path></svg></span>';
                parent.appendChild(fallback);
              }
            }}
          />
        </div>
        <Title level={4} style={{ margin: 0, color: '#262626', fontWeight: 700 }}>
          Growth<span style={{ color: '#1890ff' }}>Hub</span>
        </Title>
      </div>

      {/* Navigation */}
      <div style={{ flex: 1, margin: '0 40px' }}>
        <Menu
          theme="light"
          mode="horizontal"
          selectedKeys={[location.pathname]}
          items={menuItems}
          onClick={handleMenuClick}
          style={{ border: 'none', background: 'transparent', lineHeight: '62px' }}
        />
      </div>

      {/* User Profile */}
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Dropdown menu={{ items: userMenu }} placement="bottomRight" trigger={['click']}>
          <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', padding: '4px 8px', borderRadius: '8px', transition: 'all 0.3s' }} className="user-profile-trigger">
            <div style={{ textAlign: 'right', marginRight: 12, lineHeight: 1.2 }}>
              <Text strong style={{ display: 'block', color: '#262626' }}>{user?.name || 'User'}</Text>
              <Text type="secondary" style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {isAdmin ? 'Administrator' : 'Enterprise Learner'}
              </Text>
            </div>
            <Avatar size="large" style={{ backgroundColor: '#001529', color: '#fff', border: '2px solid #f0f0f0' }}>
              {user?.name ? user.name.charAt(0).toUpperCase() : <UserOutlined />}
            </Avatar>
            <DownOutlined style={{ fontSize: '10px', marginLeft: 8, color: '#bfbfbf' }} />
          </div>
        </Dropdown>
      </div>
    </AntHeader>
  );
};

export default Header;